import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import CircularProgress from '@mui/material/CircularProgress';

import { forgot } from '../../api';
import { AuthForm, AuthFormButton, AuthFormInput, AuthFormLabel } from '../../features/auth';
import logo from '../../images/logo-login.png';
import { translateAuthError } from '../../lib/authError';
import LanguageControl from '../../shared/components/LanguageControl';

function Forgot(_props: any) {
    const { t, i18n } = useTranslation(undefined, { keyPrefix: 'Login' });

    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [stage, setStage] = useState('entry');
    const [emailDisplay, setEmailDisplay] = useState('');

    const onSubmit = (event: any) => {
        setLoading(true);
        event.preventDefault();
        const email = event.target.email.value;

        forgot(email, i18n.language)
            .then((data: any) => {
                if (data && data.success) {
                    setLoading(false);
                    setEmailDisplay(email);
                    setStage('success');
                } else {
                    // Legacy path: backend returns 200 with { error } in body.
                    // Note: /forgot deliberately always returns success (privacy —
                    // no user enumeration), so this branch is effectively dead
                    // today; kept for shape parity with the other auth pages.
                    setLoading(false);
                    alert(translateAuthError(t, data, 'authError.requestFailed'));
                }
            })
            .catch((error) => {
                // Rejects here on network failures (and any future non-200
                // response the backend might introduce). Fallback covers the
                // no-response case.
                setLoading(false);
                alert(translateAuthError(t, error, 'authError.requestFailed'));
            });
    };

    const redirectToLogin = () => {
        navigate('/login');
    };

    if (loading) {
        return <CircularProgress />;
    }

    return (
        <div className='container-auth relative'>
            <AuthForm
                className={`${stage === 'entry' ? 'flex' : 'hidden'}`}
                logoSrc={logo}
                onLogoClick={redirectToLogin}
                topRight={<LanguageControl className='hidden md:block' />}
            >
                <p className='mt-16 mb-4 text-xl font-semibold text-[#757575] md:text-gray-700 md:font-bold'>
                    {t('reset')}
                </p>
                <p className='mb-4 w-[320px] text-center text-[10px] text-gray-400 md:w-[90%] md:text-sm'>
                    {t('resetNote1')}
                    <br />
                    {t('resetNote2')}
                </p>
                <form onSubmit={onSubmit} className='flex w-[360px] flex-col items-center md:w-full'>
                    <AuthFormLabel htmlFor='email'>{t('email')}</AuthFormLabel>
                    <AuthFormInput id='email' name='email' type='text' autoComplete='username' />
                    <AuthFormButton className='md:w-[10rem] w-full'>{t('send')}</AuthFormButton>
                </form>
                <div className='md:absolute md:bottom-2 md:left-1/2 md:-translate-x-1/2 mt-12 w-full text-center md:mt-0'>
                    <p className='text-sm'>
                        {t('question')}{' '}
                        <a href='mailto:support@gocochain.com' className='text-blue-600 hover:underline'>
                            support@gocochain.com
                        </a>
                    </p>
                </div>
                <div className='block md:hidden'>
                    <LanguageControl />
                </div>
            </AuthForm>

            <AuthForm
                className={`${stage === 'success' ? 'flex' : 'hidden'} gap-y-[8px]`}
                logoSrc={logo}
                onLogoClick={redirectToLogin}
                topRight={<LanguageControl className='hidden md:block' />}
            >
                <p className='mt-20 mb-8 text-xl font-bold text-[#757575] md:text-gray-700'>{t('mailSuccess')}</p>
                <p className='mb-1 w-[90%] text-center text-sm text-gray-400'>{t('resetSuccessNote')}</p>
                <p className='text-sm text-blue-600'>{emailDisplay}</p>
                <div className='md:mt-24 md:flex md:w-full md:justify-center max-md:px-[16px] max-md:py-[24px]'>
                    <AuthFormButton
                        type='button'
                        className='md:w-[10rem] max-md:p-[8px]'
                        onClick={() => navigate('/login')}
                    >
                        {t('return')}
                    </AuthFormButton>
                </div>
                <div className='md:absolute md:bottom-2 md:left-1/2 md:-translate-x-1/2 w-full text-center'>
                    <p className='text-sm'>
                        {t('question')}{' '}
                        <a href='mailto:support@gocochain.com' className='text-blue-600 hover:underline'>
                            support@gocochain.com
                        </a>
                    </p>
                </div>
                <div className='block md:hidden'>
                    <LanguageControl />
                </div>
            </AuthForm>
            <p className='absolute bottom-2 left-0 right-0 w-full px-2 text-center text-xs text-brandGray font-roboto'>
                © 2024 Gocochain Co., Ltd. All rights reserved.
            </p>
        </div>
    );
}

export default Forgot;
